import { ReactNode, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Paper,
    Tab,
    Stack,
    Typography,
    Divider,
    Chip
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingContainer } from "../../app/LoadingContainer";
import { selectSettings } from "../settings/settingsSlice";
import DevicesIcon from "@mui/icons-material/Devices";
import CategoryIcon from "@mui/icons-material/Category";
import FactoryIcon from "@mui/icons-material/Factory";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
    selectDeviceById,
    Device, loadDeviceDetails
} from "./deviceSlice";
import { ObjectValidationStatusChip } from "../computerizedSystems/components/ObjectValidationStatusChip";
import DnsIcon from "@mui/icons-material/Dns";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Attachements } from "../urs/Attachements";
import { BlobResourceType } from "../documents/blobResourceTypes";
import TagIcon from '@mui/icons-material/Tag';
import { StructureName } from "./Structure";
import { DeviceValidationsPanel } from "./tabs/DeviceValidationsPanel";
import { APIError } from "../../app/appTypes";
import { unwrapResult } from "@reduxjs/toolkit";
import { ServerErrorMsg } from "../../app/ServerErrorMsg";
import React from "react";
import { EditDeviceField } from "./dialogs/EditDeviceField";
import { EditDeviceSerialNoField } from "./dialogs/EditDeviceSerialNoField";
import { FieldKey, KeyValueRow } from "./controls/KeyValueRow";
import { EntityBreadcrumbs } from "../../app/components/EntityBreadcrumbs";

type FormTabs = "base" | "processes" | "attachments"

export const DeviceForm = () => {
    const { id: paramId } = useParams<{ id: string }>();
    const id = paramId ?? ''
    const dispatch = useAppDispatch();
    const device = useAppSelector(state => selectDeviceById(state, id));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [err, setErr] = useState<APIError | null>(null)

    useEffect(() => {
        if (paramId && !device) {
            setIsLoading(true);
            dispatch(loadDeviceDetails(paramId))
                .then(unwrapResult)
                .catch(e => setErr(e as APIError))
                .finally(() => {
                    setIsLoading(false);
                })
        }
    }, [device, dispatch, paramId])


    const handleUpdated = useCallback((updated: Device) => {
    }, []);


    if (isLoading) return <LoadingContainer />;

    if (err) return <ServerErrorMsg err={err} />

    if (!device) return <span>No device</span>

    return <DeviceFormInternal device={device} onUpdated={handleUpdated} editable={true} />;
};

export const DeviceFormInternal = ({
    device
}: {
    device: Device;
        onUpdated: (updated: Device) => void;
        editable: boolean
}) => {
    const [tab, setTab] = useState<FormTabs>("base");

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    return (
        <Paper elevation={0} sx={{ p: 0 }}>
            <EntityBreadcrumbs current={{ type: "device", id: device.id }} />
            <DeviceHead device={device} />

            <TabContext value={tab}>
                <TabList onChange={handleTabChange}>
                    <Tab
                        id="0"
                        value="base"
                        label={<Localized id="device-base-info-tab">_Dane</Localized>} />
                    <Tab
                        id="1"
                        value="processes"
                        label={<Localized id="device-validation-tab">_Validations</Localized>} />
                    <Tab
                        id="2"
                        value="attachments"
                        label={<Localized id="attachments">_Attachments</Localized>} />
                </TabList>
                <TabPanel value={"base"} sx={{ p: 0, pt: 2 }}>
                    <DeviceGeneralInfo device={device} editable={true} />
                </TabPanel>
                <TabPanel value={"processes"} sx={{ p: 0, pt: 2 }}>
                    <DeviceValidationsPanel deviceId={device?.id} />
                </TabPanel>
                <TabPanel value={"attachments"} sx={{ p: 0, pt: 2 }}>
                    <Attachements resourceId={device?.guid} resourceType={BlobResourceType.Device} canDelete={true} canUpload={true} />

                </TabPanel>
            </TabContext>

            <Divider />
        </Paper>
    );
};


const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
        {children}
    </Paper>
);

export const DeviceHead = (props: { device: Device }) => {
    const { device } = props
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const lang = locale === "pl" ? "pl" : "en";

    const name = device.name ?? "";
    const isGxpRelevant = device.isGxpRelevant ?? false;

    return <Box
            sx={{
                px: 0.5,
                py: 1.5,
                bgcolor: "grey.100",
            }}
        >
            <Box
                sx={{
                    px: 1,
                    py: 1,
                    bgcolor: "grey.100",
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 1 }}
                >
                    <Chip
                        icon={<DevicesIcon fontSize="small" />}
                        color="info"
                        label={
                            <Localized id="devices-device">
                                <span>_Urządzenie</span>
                            </Localized>
                        }
                        variant="outlined"
                    />
                </Stack>

                <Box
                    sx={(t) => ({
                        bgcolor: "white",
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            fontSize: 20,
                        }}
                    >
                        {name || "—"}
                    </Typography>
                </Box>
            </Box>

            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
                divider={
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ display: { xs: "none", sm: "block" } }}
                    />
                }
                alignItems={{ xs: "flex-start", sm: "center" }}
            >
                {/* Typ urządzenia */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: 1 }}>
                    <CategoryIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="devices-type">Typ</Localized>:
                    </Typography>
                    <Typography variant="body2">
                        {device.structureId ? <StructureName structureId={device.structureId} /> : "—"}

                    </Typography>
                </Stack>

                {/* Numer seryjny */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <TagIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="devices-serial-number">
                            _Numer seryjny
                        </Localized>
                        :
                    </Typography>
                    <Typography variant="body2">
                        {device.serialNo || "—"}
                    </Typography>
                </Stack>

                {/* Producent */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <FactoryIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="devices-producer">Producent</Localized>:
                    </Typography>
                    <Typography variant="body2">
                        {device.manufacturer
                            || "—"}
                    </Typography>
                </Stack>

                {/* Status walidacji */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <FactCheckIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        <Localized id="cs-validation-status">
                            Status walidacji
                        </Localized>
                        :
                    </Typography>
                    <ObjectValidationStatusChip
                        status={device.validationStatus as any}
                        lang={lang}
                    />
                </Stack>

                {/* GxP */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircleOutlineIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        GxP:
                    </Typography>
                    <Typography variant="body2">
                        {isGxpRelevant ? l10n.getString("yes", null, "_yes") : l10n.getString("no", null, "_no") }
                    </Typography>
                </Stack>
            </Stack>
        </Box>
}

export const DeviceGeneralInfo = (props: { device: Device, editable: boolean }) => {
    const { device, editable } = props

    const name = device.name ?? "";
    const model = device.model ?? "";
    const serialNo = device.serialNo ?? "";
    const productionYear = device.productionYear;
    const supplierName = device.supplierName;
    const description = device.description ?? "";
    const isGxpRelevant = device.isGxpRelevant ?? false;
    const unknownNo = device.unknownNo ?? false;

    const { l10n } = useLocalization()

    const [editField, setEditField] = React.useState<FieldKey | null>(null);
    const [editValue, setEditValue] = React.useState<ReactNode>("");

    const openEdit = (field: FieldKey, value?: ReactNode | null) => {
        setEditField(field);
        setEditValue(value ?? "");
    };

    const closeEdit = () => setEditField(null);

    return <Section>
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={(t) => ({
                mb: 1,
                bgcolor: "grey.100",
                p: 1,
                borderRadius: 1,
                border: `1px solid ${t.palette.divider}`,
            })}
        >
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
            >
                <DnsIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    <Localized id="cs-section-basic">
                        Podstawowe informacje
                    </Localized>
                </Typography>
            </Stack>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />

        <KeyValueRow 
            field="name"
            label={<Localized id="devices-name">_Nazwa urządzenia</Localized>} 
            value={name || "—"}
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="structureId" 
            label={<Localized id="devices-type">_Typ</Localized>} 
            value={device.structureId ? <StructureName structureId={device.structureId} /> : "—"}
            editable={editable} 
            openEdit={openEdit} />
        <KeyValueRow 
            field="manufacturer" 
            label={<Localized id="devices-producer">_Producent</Localized>} 
            value={device.manufacturer || "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="model" 
            label={<Localized id="devices-model">_Model</Localized>} 
            value={model || "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="serialNo" 
            label={<Localized id="devices-serial-number">_Numer fabryczny</Localized>} 
            value={unknownNo ? l10n.getString("device-unknown-no", null, "_Nieznany numer fabryczny") : serialNo || "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="productionYear" 
            label={<Localized id="devices-production-year">_Rok produkcji</Localized>} 
            value={productionYear ? productionYear.toString() : "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="supplier" 
            label={<Localized id="supplier">_Dostawca</Localized>} 
            value={supplierName || "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="description" 
            label={<Localized id="devices-description">_Opis</Localized>} 
            value={description || "—"} 
            editable={editable}
            openEdit={openEdit} />
        <KeyValueRow 
            field="isGxpRelevant" 
            label="GxP"
            value={<Chip
                size="small"
                label={
                    isGxpRelevant
                        ? l10n.getString("yes", null, "_Yes")
                        : l10n.getString("no", null, "_No")
                }
                color={isGxpRelevant ? "success" : "default"}
                variant="outlined"
            />} 
            editable={editable}
            openEdit={openEdit} />
        {editable && editField && (
            editField === "serialNo" ? (
                <EditDeviceSerialNoField
                    device={device}
                    onSave={closeEdit}
                    onCancel={closeEdit}
                />
            ) : (
                <EditDeviceField
                    device={device}
                    field={editField}
                    value={editValue}
                    onSave={closeEdit}
                    onCancel={closeEdit}
                />
            )
        )}
    </Section>
}
